import React, { forwardRef, ReactNode } from 'react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';

import { getLinkTarget } from '@lyearn/core/utils/iframe';
import { isExternalAppPath } from '@/pages/corporate/routes/external-app';

interface PressableStateCallbackType {
  readonly pressed: boolean;
}

export interface LinkProps extends Omit<NextLinkProps, 'href'> {
  to?: NextLinkProps['href'];
  href?: NextLinkProps['href'];
  children?: ReactNode | undefined | ((config: PressableStateCallbackType) => ReactNode);
  className?: string;
  target?: string;
  onClick?: React.MouseEventHandler;
  showHighlight?: boolean;
}

const DEFAULT_PRESSABLE_STATE: PressableStateCallbackType = { pressed: false };
const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { to, href, replace, children, className, target, ...rest } = props;
  const link = (to || href)!;

  let linkTarget = getLinkTarget(link, target);

  // next link crashes if link is undefined
  if (!link) {
    return <>{typeof children === 'function' ? children(DEFAULT_PRESSABLE_STATE) : children}</>;
  }

  // `/w/*` and `/a/*` are served by sibling SPAs, so the Next router cannot route into them.
  // Those links need a real anchor and a full page load; the next-only props would warn on a plain <a>.
  if (typeof link === 'string' && isExternalAppPath(link)) {
    const { prefetch, scroll, shallow, locale, legacyBehavior, passHref, as, ...anchorProps } =
      rest;

    return (
      <a
        {...anchorProps}
        ref={ref}
        className={className}
        href={link}
        target={linkTarget}
        onClick={(event) => {
          anchorProps.onClick?.(event);

          // A plain anchor always pushes, so `replace` has to be honoured by hand.
          if (replace && !event.defaultPrevented) {
            event.preventDefault();
            window.location.replace(link);
          }
        }}>
        {typeof children === 'function' ? children(DEFAULT_PRESSABLE_STATE) : children}
      </a>
    );
  }

  return (
    <NextLink
      ref={ref}
      className={className}
      href={link}
      replace={replace}
      target={linkTarget}
      {...rest}>
      {typeof children === 'function' ? children(DEFAULT_PRESSABLE_STATE) : children}
    </NextLink>
  );
});

Link.displayName = 'Link';

export default Link;
